// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, useEffect, useMemo, useState, useCallback, memo } from 'react'
import { useDebounce } from 'use-debounce'

// others
import {
  Case,
  CaseStatus,
  Maybe,
  OrderCaseBy,
  useFetchAllMattersWithoutJoinLazyQuery,
  useFetchCaseLazyQuery
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT } from '../../../constants'

interface DepositMatterDropdownProps {
  matterId?: string
  onChange: (matter: Maybe<Case>) => void
  disabled?: boolean
  autoSelectOnTab?: boolean
  required?: boolean
}

const DepositMatterDropdown: FC<DepositMatterDropdownProps> = ({
  matterId,
  onChange,
  disabled,
  autoSelectOnTab = false,
  required = false
}) => {
  const [cases, setCases] = useState<Maybe<Case>[]>([])
  const [casePage, setCasePage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [caseSearchTerm, setCaseSearchTerm] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')
  const [selectedCase, setSelectedCase] = useState<Maybe<Case>>(null)

  const [debouncedCaseSearchTerm] = useDebounce(caseSearchTerm, 500)

  const [fetchCaseById] = useFetchCaseLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const caseData = data?.fetchCase?.case
      if (caseData?.id) {
        const c = caseData as Case
        setSelectedCase(c)
        onChange(c)
      }
    }
  })

  const [fetchCases, { loading }] = useFetchAllMattersWithoutJoinLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,

    onError() {
      setCases([])
      setHasMorePages(false)
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllMattersWithoutJoin: fetchAllCases } = data

        if (fetchAllCases) {
          const { cases: newCases, pagination } = fetchAllCases
          const { page: currentPageNo, totalPages } = pagination || {}

          const newHasMore = currentPageNo && totalPages ? currentPageNo < totalPages : false

          if (newCases) {
            setCases(prevCases => {
              const updatedCases = casePage === 1 ? newCases : [...(prevCases || []), ...newCases]
              return updatedCases
            })

            setHasMorePages(newHasMore)
          } else {
            setHasMorePages(false)
          }
        }
      }
    }
  })

  // Fetch initial case if matterId is provided from URL
  useEffect(() => {
    if (matterId && !selectedCase) {
      fetchCaseById({
        variables: { caseInput: { id: matterId } }
      })
    }

    // Clear state if matterId is undefined
    if (matterId === undefined && selectedCase) {
      setSelectedCase(null)
      setInputValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId])

  useEffect(() => {
    setCases([])
    setCasePage(1)
    setHasMorePages(true)
  }, [debouncedCaseSearchTerm])

  useEffect(() => {
    const searchValue = debouncedCaseSearchTerm.trim()

    hasMorePages &&
      fetchCases({
        variables: {
          casesInput: {
            orderBy: OrderCaseBy.Recent,
            status: CaseStatus.Active,
            searchValue: searchValue,
            isConsultCase: undefined,
            paginationOptions: {
              limit: PAGE_LIMIT,
              page: casePage
            }
          }
        }
      })
  }, [casePage, debouncedCaseSearchTerm, fetchCases, hasMorePages])

  const options = useMemo(() => {
    return cases || []
  }, [cases])

  const getOptionLabel = (option: Case) => {
    const caseNumber = option?.isConsultMatter ? option.consultMatterNumber : option?.caseNumber
    const title = option?.title || ''

    if (caseNumber) return `(#${caseNumber}) ${title}`
    return title
  }

  // Set input value when selectedCase changes
  useEffect(() => {
    if (selectedCase && selectedCase.title) {
      const label = getOptionLabel(selectedCase as Case)
      setInputValue(label)
    }
  }, [selectedCase])

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
        hasMorePages &&
        !loading
      ) {
        setCasePage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  const handleInputChange = useCallback((event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setCaseSearchTerm(newInputValue)
      setInputValue(newInputValue)
    }
  }, [])

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: Maybe<Case> | null) => {
      if (newValue) {
        setSelectedCase(newValue)
        onChange(newValue)
        setCaseSearchTerm('')
        const label = getOptionLabel(newValue as Case)
        setInputValue(label)
        // Cases from the list (fetchAllMattersWithoutJoin) don't include caseToExternalUserToExternalUserRole.
        // Fetch full case so the parent gets primary client for trust/escrow/client payment.
        const hasClientData =
          newValue.caseToExternalUserToExternalUserRole &&
          (newValue.caseToExternalUserToExternalUserRole as unknown[]).length > 0
        if (!hasClientData && newValue.id) {
          fetchCaseById({
            variables: { caseInput: { id: newValue.id } }
          })
        }
      } else {
        setSelectedCase(null)
        onChange(null)
        setInputValue('')
        setCaseSearchTerm('')
      }
    },
    [onChange, fetchCaseById]
  )

  return (
    <Autocomplete
      options={options}
      getOptionLabel={option => getOptionLabel(option as Case)}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      onChange={(event, value) => handleChange(event, value as Maybe<Case> | null)}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      disabled={disabled}
      value={selectedCase}
      autoHighlight={autoSelectOnTab}
      autoSelect={autoSelectOnTab}
      renderInput={params => <TextField {...params} label="Select Matter" fullWidth required={required} />}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '190px' },
        onScroll: handleScroll
      }}
      sx={{ width: '100%' }}
      noOptionsText={loading ? 'Loading...' : cases?.length === 0 ? 'No Matters' : 'No options'}
      loading={loading}
      freeSolo={false}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      disableCloseOnSelect={false}
      clearText="Clear"
      clearOnEscape
      disableClearable={false}
    />
  )
}

export default memo(DepositMatterDropdown)
